import {
  Button,
  Group,
  Select,
  Stack,
  Textarea,
  Accordion,
  Box,
  Text,
} from '@mantine/core';
import { memo, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  Eye,
  CornerDownLeft,
} from 'lucide-react';

interface RichTextareaProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  placeholder?: string;
}

// Unity TextMeshPro supported tags + custom tags
const CUSTOM_TAGS = [
  { value: '[CODICEUNIVOCOLICENZA]', label: 'Codice Licenza' },
  { value: '[GTIN]', label: 'GTIN' },
  { value: '[BREAK]', label: 'Break (Array)' },
];

export const RichTextarea = memo(function RichTextarea({
  value,
  onChange,
  rows,
  minRows = 3,
  maxRows = 10,
  placeholder,
}: RichTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const insertText = (before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText =
      value.substring(0, start) +
      before +
      selectedText +
      after +
      value.substring(end);

    onChange(newText);

    // Set cursor position after inserted text
    setTimeout(() => {
      const newPosition =
        start + before.length + selectedText.length + after.length;
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const wrapWithTag = (tag: string) => {
    insertText(`<${tag}>`, `</${tag}>`);
  };

  const insertCustomTag = (tag: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const newText = value.substring(0, start) + tag + value.substring(start);

    onChange(newText);
    setSelectedTag(null);

    // Set cursor position after tag
    setTimeout(() => {
      const newPosition = start + tag.length;
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Render preview - converts Unity tags to HTML
  const renderPreview = (text: string) => {
    let html = text;

    // Convert Unity TextMeshPro tags to HTML
    html = html.replace(/<b>(.*?)<\/b>/g, '<strong>$1</strong>');
    html = html.replace(/<i>(.*?)<\/i>/g, '<em>$1</em>');
    html = html.replace(/<u>(.*?)<\/u>/g, '<u>$1</u>');
    html = html.replace(/<s>(.*?)<\/s>/g, '<s>$1</s>');
    html = html.replace(/<mark>(.*?)<\/mark>/g, '<mark>$1</mark>');
    html = html.replace(/<sup>(.*?)<\/sup>/g, '<sup>$1</sup>');
    html = html.replace(/<sub>(.*?)<\/sub>/g, '<sub>$1</sub>');
    html = html.replace(
      /<align=center>(.*?)<\/align>/g,
      '<div style="text-align: center">$1</div>'
    );
    html = html.replace(
      /<align=left>(.*?)<\/align>/g,
      '<div style="text-align: left">$1</div>'
    );
    html = html.replace(
      /<align=right>(.*?)<\/align>/g,
      '<div style="text-align: right">$1</div>'
    );

    // Custom tags - render in a different color
    html = html.replace(
      /\[([^\]]+)\]/g,
      '<code style="color: orange; background: #fff3e0; padding: 2px 6px; border-radius: 4px">[$1]</code>'
    );

    // Preserve line breaks
    html = html.replace(/\n/g, '<br>');

    return html;
  };

  return (
    <Stack gap='xs'>
      <Group gap='xs' wrap='wrap'>
        {/* Unity TextMeshPro formatting buttons */}
        <Button
          size='xs'
          variant='light'
          onClick={() => wrapWithTag('b')}
          leftSection={<Bold size={14} />}
          title='Grassetto <b>testo</b>'
        >
          Bold
        </Button>
        <Button
          size='xs'
          variant='light'
          onClick={() => wrapWithTag('i')}
          leftSection={<Italic size={14} />}
          title='Corsivo <i>testo</i>'
        >
          Italic
        </Button>
        <Button
          size='xs'
          variant='light'
          onClick={() => wrapWithTag('u')}
          leftSection={<Underline size={14} />}
          title='Sottolineato <u>testo</u>'
        >
          Underline
        </Button>
        <Button
          size='xs'
          variant='light'
          onClick={() => wrapWithTag('s')}
          title='Barrato <s>testo</s>'
        >
          <s>S</s>
        </Button>
        <Button
          size='xs'
          variant='light'
          onClick={() => wrapWithTag('mark')}
          title='Evidenziato <mark>testo</mark>'
        >
          <mark>Mark</mark>
        </Button>
        <Button
          size='xs'
          variant='light'
          onClick={() => wrapWithTag('sup')}
          title='Apice <sup>testo</sup>'
        >
          X<sup>2</sup>
        </Button>
        <Button
          size='xs'
          variant='light'
          onClick={() => wrapWithTag('sub')}
          title='Pedice <sub>testo</sub>'
        >
          X<sub>2</sub>
        </Button>

        {/* Alignment */}
        <Button
          size='xs'
          variant='light'
          onClick={() => insertText('<align=center>', '</align>')}
          leftSection={<AlignLeft size={14} />}
          title='Allineamento <align=center>testo</align>'
        >
          Center
        </Button>

        {/* Line break */}
        <Button
          size='xs'
          variant='light'
          onClick={() => insertText('\n', '')}
          leftSection={<CornerDownLeft size={14} />}
          title='A capo'
        >
          Break
        </Button>

        {/* Custom tags select */}
        <Select
          placeholder='Inserisci Tag'
          data={CUSTOM_TAGS}
          value={selectedTag}
          onChange={val => val && insertCustomTag(val)}
          size='xs'
          w={180}
          color='orange'
          clearable
        />
      </Group>

      <Textarea
        ref={textareaRef}
        value={value}
        onChange={e => onChange(e.currentTarget.value)}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        autosize
        placeholder={placeholder}
      />

      {/* Preview accordion */}
      {value && (
        <Accordion variant='contained'>
          <Accordion.Item value='preview'>
            <Accordion.Control icon={<Eye size={16} />}>
              Anteprima Formattazione
            </Accordion.Control>
            <Accordion.Panel>
              <Box
                p='md'
                style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '60px',
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: renderPreview(value) }}
                />
              </Box>
              <Text size='xs' c='dimmed' mt='xs'>
                Questa è un'anteprima approssimativa. Il rendering finale
                potrebbe variare in Unity.
              </Text>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
    </Stack>
  );
});
